import { BUTTON_HEIGHT } from 'components/Button';
import { ICONS } from 'components/Icon';
import { IconButton } from 'components/IconButton';
import { Txt } from 'components/Txt';
import { noop } from 'lodash';
import { theme } from 'lib/theme';
import React from 'react';
import { StyleProp, View, ViewStyle } from 'react-native';
import { TextField } from './TextField';

type Props = {
  value: number | undefined;
  onChange: (number: number) => void;
  style?: StyleProp<ViewStyle>;
  minValue?: number;
  small?: boolean;
};

export const NumberSpinner: React.FC<Props> = React.memo(
  ({ value, onChange, style, minValue = 1, small }) => {
    const [number, setNumber] = React.useState<number | undefined>(value);
    const [fieldPrefixKeyRef, setFieldPrefixKeyRef] = React.useState<number>(0);
    const timeoutRef = React.useRef<ReturnType<typeof setTimeout>>(null);
    const spinTimeoutRef = React.useRef<ReturnType<typeof setTimeout>>(null);
    const spinIntervalRef = React.useRef<ReturnType<typeof setInterval>>(null);
    const fieldValueRef = React.useRef<number | undefined>(undefined);
    const valueRef = React.useRef<number | undefined>(number);

    const stopSpinning = React.useCallback(() => {
      if (spinTimeoutRef.current) {
        clearTimeout(spinTimeoutRef.current);
        spinTimeoutRef.current = null;
      }
      if (spinIntervalRef.current) {
        clearInterval(spinIntervalRef.current);
        spinIntervalRef.current = null;
      }
    }, []);

    const handleChange = React.useCallback(
      (diff: number) => {
        setNumber((cur) => {
          const newValue = (cur || 0) + diff;

          if (newValue < minValue) {
            stopSpinning();
            return cur;
          }

          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }

          timeoutRef.current = setTimeout(() => onChange(newValue), 150);
          valueRef.current = newValue;
          return newValue;
        });
      },
      [minValue, onChange, stopSpinning],
    );

    const startSpinning = React.useCallback(
      (spinAmount: number) => {
        if (spinTimeoutRef.current) {
          clearTimeout(spinTimeoutRef.current);
          spinTimeoutRef.current = null;
        }
        if (spinIntervalRef.current) {
          clearInterval(spinIntervalRef.current);
          spinIntervalRef.current = null;
        }
        handleChange(spinAmount);
        spinTimeoutRef.current = setTimeout(() => {
          spinIntervalRef.current = setInterval(
            () => handleChange(spinAmount),
            100,
          );
        }, 500);
      },
      [handleChange],
    );

    const handleSpinDown = React.useCallback(() => {
      startSpinning(-1);
    }, [startSpinning]);

    const handleSpinUp = React.useCallback(() => {
      startSpinning(1);
    }, [startSpinning]);

    const handleFieldChange = React.useCallback((newValue: string) => {
      // string.replaceAll is unavailable on Android for some reason
      const newValueParsed = parseInt(
        newValue
          .split('')
          .filter((a) => a !== '.')
          .join(''),
        10,
      );
      fieldValueRef.current = !Number.isNaN(newValueParsed)
        ? newValueParsed
        : -1;
    }, []);

    const handleFieldBlur = React.useCallback(() => {
      if (
        fieldValueRef.current !== undefined &&
        fieldValueRef.current >= minValue
      ) {
        valueRef.current = fieldValueRef.current;
        setNumber(fieldValueRef.current);
        onChange(fieldValueRef.current);
      } else {
        setFieldPrefixKeyRef(Math.round(Math.random() * 1000));
      }
    }, [minValue, onChange]);

    const handleFieldFocus = React.useCallback(() => {
      fieldValueRef.current = undefined;
    }, []);

    React.useEffect(() => {
      return () => {
        if (spinTimeoutRef.current) {
          clearTimeout(spinTimeoutRef.current);
          spinTimeoutRef.current = null;
        }
        if (spinIntervalRef.current) {
          clearInterval(spinIntervalRef.current);
          spinIntervalRef.current = null;
        }
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      };
    }, []);

    return (
      <View
        style={[
          {
            height: BUTTON_HEIGHT,
            borderWidth: 1,
            borderColor: theme.colors.blue80,
            borderRadius: 4,
            overflow: 'hidden',
            alignItems: 'center',
            flexDirection: 'row',
            backgroundColor: '#ffffff',
          },
          small ? { width: 70 } : { width: 120 },
          style,
        ]}
      >
        <IconButton
          icon={ICONS.MinusLight}
          size={BUTTON_HEIGHT}
          iconSize={15}
          style={[
            {
              borderRadius: 0,
              height: '100%',
            },
            small
              ? { flex: 1, paddingLeft: 5, alignItems: 'flex-start', zIndex: 1 }
              : undefined,
          ]}
          disabled={(number || 0) <= minValue}
          onPress={noop}
          onPressIn={number === minValue ? undefined : handleSpinDown}
          onPressOut={stopSpinning}
        />
        {small ? (
          <Txt
            style={{
              marginTop: 2,
              flex: 1,
              textAlign: 'center',
              position: 'absolute',
              width: '100%',
            }}
          >
            {number || number === 0 ? Math.max(number, 0) : 'NV'}
          </Txt>
        ) : (
          <TextField
            value={number || number === 0 ? `${number}` : ''}
            key={`${fieldPrefixKeyRef}-${number}`}
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
            onFocus={handleFieldFocus}
            clearTextOnFocus
            onChangeDebounceMs={0}
            inputMode="numeric"
            containerStyle={{
              height: '100%',
              flex: 1,
              width: '100%',
              padding: 0,
              margin: 0,
            }}
            inputStyle={{
              textAlign: 'center',
              height: '100%',
              minHeight: undefined,
              flex: 1,
              paddingHorizontal: 0,
              margin: 0,
              padding: 0,
              width: '100%',
              borderLeftWidth: 1,
              borderLeftColor: theme.colors.blue80,
              borderRightWidth: 1,
              borderRightColor: theme.colors.blue80,
            }}
          />
        )}
        <IconButton
          icon={ICONS.PlusLight}
          size={BUTTON_HEIGHT}
          iconSize={15}
          style={[
            {
              borderRadius: 0,
              height: '100%',
            },
            small
              ? { flex: 1, paddingRight: 5, alignItems: 'flex-end', zIndex: 1 }
              : undefined,
          ]}
          onPress={noop}
          onPressIn={handleSpinUp}
          onPressOut={stopSpinning}
        />
      </View>
    );
  },
);
